import {
  applyCarrierMoveToOrder,
  deriveCarrierMove,
  extractAllCarrierIdsAlphabetical,
  useCarrierSequence,
  type Proposal,
} from "@platform/mpx-quotes-grid";
import { CoverageTypeEnum } from "@platform/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCarrierSequenceContext } from "../../../CarrierSequenceContext";

import { REQUESTED_CARRIER_ID } from "./useQuotesPresentationGridData";

import type useToastNotificationService from "../../../../../../../hooks/useToastNotificationService";

// ---------------------------------------------------------------------------
// Carrier ordering — extracted to keep Quotes below the complexity limit
// ---------------------------------------------------------------------------

export const useQuotesCarrierOrdering = ({
  proposals,
  selectedCoverageType,
  effectiveShowHistory,
  createToastNotification,
}: {
  proposals: Proposal[];
  selectedCoverageType: CoverageTypeEnum | undefined;
  effectiveShowHistory: boolean;
  createToastNotification: ReturnType<
    typeof useToastNotificationService
  >["createToastNotification"];
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- inferred; extracted interface would be large
}) => {
  const {
    getSequenceState,
    setSequenceState: setSharedSequenceState,
    confirmApplyToAllPages,
    appliedToAllCarrierOrder,
    setAppliedToAllCarrierOrder,
  } = useCarrierSequenceContext();

  // Read the stored order for the active coverage type from the shared context.
  // This value is read once per coverageType change and passed as initialOrder
  // so the hook can seed its local state without an extra render cycle.
  const sharedOrder = useMemo(
    () =>
      selectedCoverageType ? getSequenceState(selectedCoverageType) : undefined,
    [selectedCoverageType, getSequenceState],
  );

  const {
    carrierIdOrder,
    isCustomOrder,
    isProjectedOrder,
    lastValidationError,
    clearValidationError,
    setCarrierOrder: setCarrierOrderInternal,
    resetOrder: resetCarrierOrder,
    hasPendingOrder,
    commitPendingOrder,
    lastChangeSource,
  } = useCarrierSequence({
    proposals,
    coverageType: selectedCoverageType ?? "",
    initialOrder: sharedOrder,
    lockedLeadingCarrierId: REQUESTED_CARRIER_ID,
    canonicalOrder: appliedToAllCarrierOrder,
  });

  // The plan order on SBS pages is always consistent from page to page, so
  // every reorder is committed and applied to all pages immediately — no
  // confirmation prompt.
  useEffect(() => {
    if (hasPendingOrder) {
      commitPendingOrder();
    }
  }, [hasPendingOrder, commitPendingOrder]);

  // Reset carrier sequence when history mode toggles so that the AG Grid
  // onColumnMoved event fired by the column rebuild doesn't trigger a
  // spurious reorder. Skip on mount — only reset on actual toggles.
  const isMountRef = useRef(true);
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    resetCarrierOrder();
  }, [effectiveShowHistory, resetCarrierOrder]);

  // Carrier order used for building grid column definitions.
  // When the reorder came from a drag, AG Grid already moved the columns
  // visually so we skip the columnDefs rebuild to prevent value overlap.
  const [carrierIdOrderForGrid, setCarrierIdOrderForGrid] = useState<
    string[] | undefined
  >(() => (carrierIdOrder.length > 0 ? carrierIdOrder : undefined));

  useEffect(() => {
    if (lastChangeSource !== "drag") {
      // Only pass an explicit order when it's either a user's custom order
      // or a canonical-order projection that actually changed something —
      // otherwise carrierIdOrder is just this LOC's own plain default, and
      // passing it explicitly (vs. undefined) changes downstream grid-data
      // building behavior even though the resulting order is identical.
      setCarrierIdOrderForGrid(
        (isCustomOrder || isProjectedOrder) && carrierIdOrder.length > 0
          ? carrierIdOrder
          : undefined,
      );
    }
  }, [carrierIdOrder, isCustomOrder, isProjectedOrder, lastChangeSource]);

  // The carrier actually dragged for the most recent reorder — set
  // synchronously by setCarrierOrder below, read by the persist effect once
  // carrierIdOrder settles. Null for a preset (which can move every carrier
  // at once, so there's no single carrier to anchor a merge on) or if AG
  // Grid couldn't identify the dragged column.
  const lastMovedCarrierIdRef = useRef<string | null>(null);
  const setCarrierOrder = useCallback(
    (carrierIds: string[], movedCarrierId: string | null = null) => {
      lastMovedCarrierIdRef.current = movedCarrierId;
      setCarrierOrderInternal(carrierIds, movedCarrierId);
    },
    [setCarrierOrderInternal],
  );

  // Show validation errors (e.g. cannot move current plan) via toast
  useEffect(() => {
    if (lastValidationError) {
      createToastNotification({
        severity: "warning",
        message: lastValidationError,
      });
      clearValidationError();
    }
  }, [lastValidationError, createToastNotification, clearValidationError]);

  // Persist the committed order to the shared context so all other SBS pages
  // (Responses, Comps, Financials) consume it. This must only ever reflect
  // an actual drag/preset action — never a side effect of switching LOC.
  //
  // Switching the LOC dropdown rebuilds this grid's columns for the new
  // coverage's carriers, and that rebuild itself fires a spurious AG Grid
  // onColumnMoved (the same pattern already worked around for the history-
  // mode toggle above) which flows through the identical onCarrierReorder →
  // setCarrierOrder path as a real drag. `coverageJustChangedRef` is set
  // synchronously during render — before any effect (ours or AG Grid's own)
  // runs — so the very next persist/toast after a LOC switch is suppressed
  // unconditionally, regardless of whether it lands as "default" or as a
  // spurious "drag".
  //
  // Only a real→real transition counts as a LOC switch here. selectedCoverageType
  // starts undefined while the RFP is still loading and settles to a real
  // value once it resolves — that settle isn't a switch (there's no prior
  // grid to have spuriously rebuilt), but it does still change the ref. Left
  // unguarded, it would arm this suppression while lastChangeSource is still
  // "default", and since only the coverageJustChanged branch below clears the
  // flag — not the "default" branch above it — it would sit armed until the
  // analyst's actual first drag and silently swallow it.
  const prevCoverageTypeForPersistRef = useRef(selectedCoverageType);
  const coverageJustChangedRef = useRef(false);
  if (
    prevCoverageTypeForPersistRef.current !== undefined &&
    prevCoverageTypeForPersistRef.current !== selectedCoverageType
  ) {
    coverageJustChangedRef.current = true;
  }
  prevCoverageTypeForPersistRef.current = selectedCoverageType;

  // Tracks the last order actually persisted so a corrective AG Grid re-fire
  // that lands after the drag-reorder guard has already reopened (see
  // carrierReorderReducer's mutationInFlight window) — which reproduces the
  // *same* order rather than a genuinely new one — doesn't persist again or
  // show a second, redundant toast.
  const lastPersistedOrderRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (lastChangeSource === "default" || !selectedCoverageType) {
      // Keep the baseline current while no reorder has happened, so the next
      // one has something to compare against. Left null until the first
      // persist, a spurious onColumnMoved — AG Grid fires one whenever it
      // rebuilds columns, and it arrives as "drag" — would pass the unchanged
      // check by default and publish this LOC's *default* carrier order to
      // every other tab as though the analyst had arranged it.
      lastPersistedOrderRef.current = carrierIdOrder;
      return;
    }
    if (coverageJustChangedRef.current) {
      coverageJustChangedRef.current = false;
      lastPersistedOrderRef.current = carrierIdOrder;
      return;
    }
    const previous = lastPersistedOrderRef.current;
    const unchanged =
      !!previous &&
      previous.length === carrierIdOrder.length &&
      previous.every((id, i) => id === carrierIdOrder[i]);
    if (unchanged) {
      return;
    }
    lastPersistedOrderRef.current = carrierIdOrder;

    // This LOC's own order — unaffected by carriers on other coverages.
    setSharedSequenceState(selectedCoverageType, {
      carrierIds: carrierIdOrder,
      appliedPreset: null,
      scope: "all",
    });

    // The cross-LOC order every other Present tab reads. This coverage's
    // carrier list is only ever a subset of the full RFP's carriers, so
    // replace it wholesale only when there's no single carrier to anchor a
    // merge on (a preset, or a drag AG Grid couldn't identify) — otherwise
    // replay just that carrier's move onto the existing global order, so
    // carriers this LOC doesn't have keep their relative position.
    const movedCarrierId = lastMovedCarrierIdRef.current;
    const previousGlobalOrder =
      appliedToAllCarrierOrder.length > 0
        ? appliedToAllCarrierOrder
        : extractAllCarrierIdsAlphabetical(proposals);
    const mergedGlobalOrder = movedCarrierId
      ? applyCarrierMoveToOrder(
          previousGlobalOrder,
          deriveCarrierMove(carrierIdOrder, movedCarrierId),
        )
      : carrierIdOrder;
    setAppliedToAllCarrierOrder(mergedGlobalOrder);

    confirmApplyToAllPages(selectedCoverageType);
    createToastNotification({
      severity: "info",
      message:
        "Carrier sequence order has been applied to all SBS pages (Responses, Comps, and Financials).",
    });
  }, [
    lastChangeSource,
    carrierIdOrder,
    selectedCoverageType,
    setSharedSequenceState,
    setAppliedToAllCarrierOrder,
    appliedToAllCarrierOrder,
    proposals,
    confirmApplyToAllPages,
    createToastNotification,
  ]);

  return {
    carrierIdOrderForGrid,
    setCarrierOrder,
  };
};
